import { presignedUrl } from '@/modules/Upload/services'
import { Attachment } from '@/modules/Upload/types'
import { ErrorResponse } from '@/types/shared'
import { sanitizeFileName } from '@/utils/function'
import { useMutationWithHandler } from './useMutationWithHandler'
import axios, { AxiosError } from 'axios'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface UseUploadFileProps {
    prefix: string
    isPublic?: boolean
    onSuccess?: (data: PromiseSettledResult<Partial<Attachment>>[]) => void
    onError?: (err: AxiosError<ErrorResponse>) => void
}
const useUploadFile = ({
    onSuccess,
    onError,
    prefix,
    isPublic = false,
}: UseUploadFileProps) => {
    const { t } = useTranslation()
    const [percent, setPercent] = useState<Record<string, number>>({})

    const uploadFile = async (file: File): Promise<Partial<Attachment>> => {
        const fileName = `${Date.now()}-${sanitizeFileName(file.name)}`
        const response = await presignedUrl({
            isPublic,
            prefix,
            key: fileName,
        })

        if (!response?.result?.url) {
            throw new Error(t(`upload-file-failed`))
        }

        await axios
            .put(response?.result?.url, file, {
                onUploadProgress: (p) => {
                    const { loaded, total } = p
                    const percent = Math.floor((loaded * 100) / (total || 1))
                    setPercent((prev) => ({
                        ...prev,
                        [file.name]: percent,
                    }))
                },
                headers: {
                    'Content-Type': file.type,
                },
            })
            .catch((err) => {
                console.log(err)
                throw new Error(t(`upload-file-failed`))
            })

        return {
            attachment_file_url: [`${prefix}`, `${fileName}`]
                .join('/')
                .replace('//', '/'),
            attachment_file_size: file.size,
            attachment_file_name: file.name,
            attachment_file_type: file.type,
        }
    }

    const uploadFiles = async (files: File[]) => {
        return Promise.allSettled(
            files.map(async (file) => {
                return await uploadFile(file)
            })
        )
    }

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: uploadFiles,
        },
        {
            reactQueryKeys: QUERY_KEYS.GENERIC,
            showSuccessToast: false,
            showErrorToast: false,
            onSuccess: (data) => onSuccess?.(data),
            onError: (err: AxiosError<ErrorResponse>) => onError?.(err),
        }
    )

    return {
        uploadAsync: mutateAsync,
        isUploading: isPending,
        percent,
    }
}

export default useUploadFile
